/**
 * Playback model for the Analytics MCP preview.
 *
 * A sample exchange arrives one step at a time — the question, the lookup, what
 * came back, then the answer — because the mechanism is the point of the page.
 * The ordering and the timing live here so the component only has to ask
 * "has this step arrived yet?".
 */

export const ANALYTICS_MCP_STEPS = ['question', 'reading', 'rows', 'answer'] as const

export type AnalyticsMcpStep = (typeof ANALYTICS_MCP_STEPS)[number]

// Milliseconds after the run starts that each step becomes visible. Slow enough
// to read as work happening, short enough that nobody waits for the answer.
const DELAY_MS_BY_STEP: Record<AnalyticsMcpStep, number> = {
  question: 0,
  reading: 400,
  rows: 1100,
  answer: 1600,
}

export function analyticsMcpStepDelay(step: AnalyticsMcpStep): number {
  return DELAY_MS_BY_STEP[step]
}

export const LAST_ANALYTICS_MCP_STEP = ANALYTICS_MCP_STEPS[ANALYTICS_MCP_STEPS.length - 1]

function analyticsMcpStepIndex(step: AnalyticsMcpStep): number {
  return ANALYTICS_MCP_STEPS.indexOf(step)
}

/**
 * Whether `step` should already be on screen for a run that has reached
 * `current`. Steps only ever move forward, so this is an index comparison.
 */
export function hasReachedAnalyticsMcpStep(
  current: AnalyticsMcpStep,
  step: AnalyticsMcpStep
): boolean {
  return analyticsMcpStepIndex(current) >= analyticsMcpStepIndex(step)
}

export function isAnalyticsMcpPlaybackComplete(current: AnalyticsMcpStep): boolean {
  return current === LAST_ANALYTICS_MCP_STEP
}
